import React, { useState } from "react";
import { motion } from 'framer-motion';
import userUploadImage from "../../medias/user-upload.png";
import tshirtPreviewFront10 from "../../medias/tshirt-preview-front10.png";
import tshirtPreviewFront8 from "../../medias/tshirt-preview-front8.png";
import tshirtPreviewBack10 from "../../medias/tshirt-preview-back10.png";
import tshirtPreviewBack8 from "../../medias/tshirt-preview-back8.png";
import tshirtProductImage from "../../medias/tshirt-product.png";
import websiteFooterLogo from "../../medias/website-footer-logo.png";
import { trackConfigurationChange, trackViewChange } from "../../../utils/analytics";

const VeeqoDemo = () => {
  const [mapTo, setMapTo] = useState("Item Notes");
  const [surfaceNames, setSurfaceNames] = useState("never");
  const [customizationField, setCustomizationField] = useState("always");
  const [itemNames, setItemNames] = useState("always");
  const [quantity, setQuantity] = useState("never");
  const [displayPreviewImage, setDisplayPreviewImage] = useState(true);
  const [displayUserUploaded, setDisplayUserUploaded] = useState(true);
  const [activeView, setActiveView] = useState("order");

  // Sample Amazon order data
  const amazonOrder = {
    items: [
      {
        name: "Soccer Shirt, Personalized Shirt for Kids, Black",
        sku: "SHRT-SCR-BLK",
        quantity: 1,
        customizations: {
          "Front": {
            "Team Name": "Panthers"
          },
          "Back": {
            "Number": "10",
            "Name": "Smith"
          }
        }
      },
      {
        name: "Soccer Shirt, Personalized Shirt for Kids, Black",
        sku: "SHRT-SCR-BLK",
        quantity: 1,
        customizations: {
          "Front": {
            "Team Name": "Tigers"
          },
          "Back": {
            "Number": "8",
            "Name": "Smith"
          }
        }
      }
    ]
  };

  // Generate Veeqo order data based on configuration
  const generateVeeqoData = () => {
    let customerNotes = "";
    let itemOptions = [];
    const hasMultipleItems = amazonOrder.items.length > 1;

    // Helper function to check if a field should be included
    const shouldInclude = (setting) => {
      if (setting === "always") return true;
      if (setting === "never") return false;
      if (setting === "only when multiple items") return hasMultipleItems;
      return false;
    };

    // Process customizations based on mapping
    amazonOrder.items.forEach((item, index) => {
      let itemNoteContent = "";
      
      // Add item name as header if enabled (for both customer notes and item notes)
      if (shouldInclude(itemNames)) {
        if (mapTo === "Customer Notes" || mapTo === "Item Notes and Customer Notes") {
          customerNotes += `${item.name}\n`;
        }
        if (mapTo === "Item Notes" || mapTo === "Item Notes and Customer Notes") {
          itemNoteContent += `${item.name}\n`;
        }
      }

      // Add quantity as header if enabled (for both customer notes and item notes)
      if (shouldInclude(quantity)) {
        if (mapTo === "Customer Notes" || mapTo === "Item Notes and Customer Notes") {
          customerNotes += `Quantity: ${item.quantity}\n`;
        }
        if (mapTo === "Item Notes" || mapTo === "Item Notes and Customer Notes") {
          itemNoteContent += `Quantity: ${item.quantity}\n`;
        }
      }

      Object.entries(item.customizations).forEach(([surface, labels]) => {
        // Add surface name as header if enabled (for both customer notes and item notes)
        if (shouldInclude(surfaceNames)) {
          if (mapTo === "Customer Notes" || mapTo === "Item Notes and Customer Notes") {
            customerNotes += `${surface}:\n`;
          }
          if (mapTo === "Item Notes" || mapTo === "Item Notes and Customer Notes") {
            itemNoteContent += `${surface}:\n`;
          }
        }
        
        Object.entries(labels).forEach(([label, value]) => {
          // For Item Notes, include all the content based on settings
          if (mapTo === "Item Notes" || mapTo === "Item Notes and Customer Notes") {
            let itemLine = "";
            if (shouldInclude(customizationField)) {
              itemLine += `${label}: `;
            }
            itemLine += value;
            itemNoteContent += itemLine + "\n";
          }
          
          // For customer notes, include customization field if enabled
          if (mapTo === "Customer Notes" || mapTo === "Item Notes and Customer Notes") {
            let customerLine = "";
            if (shouldInclude(customizationField)) {
              customerLine += `${label}: `;
            }
            customerLine += value;
            customerNotes += customerLine + "\n";
          }
        });
      });

      // Add empty line between items (except for the last item)
      if (index < amazonOrder.items.length - 1) {
        if (mapTo === "Customer Notes" || mapTo === "Item Notes and Customer Notes") {
          customerNotes += "\n";
        }
        if (mapTo === "Item Notes" || mapTo === "Item Notes and Customer Notes") {
          itemNoteContent += "\n";
        }
      }

      // Add the complete item note content to itemOptions
      if (mapTo === "Item Notes" || mapTo === "Item Notes and Customer Notes") {
        itemOptions.push({ 
          itemIndex: index, 
          content: itemNoteContent.trim(),
          isHeader: false 
        });
      }
    });

    return {
      customerNotes: customerNotes.trim(),
      itemOptions: itemOptions
    };
  };

  const veeqoData = generateVeeqoData();

  // Helper function to get the appropriate front preview image based on item customization
  const getFrontPreviewImage = (item) => {
    // Check if the item has a "Number" customization with value "10"
    if (item.customizations.Back && item.customizations.Back.Number === "10") {
      return tshirtPreviewFront10;
    }
    // Check if the item has a "Number" customization with value "8"
    if (item.customizations.Back && item.customizations.Back.Number === "8") {
      return tshirtPreviewFront8;
    }
    // Return null if no matching front image
    return null;
  };

  // Helper function to get the appropriate back preview image based on item customization
  const getBackPreviewImage = (item) => {
    // Check if the item has a "Number" customization with value "10"
    if (item.customizations.Back && item.customizations.Back.Number === "10") {
      return tshirtPreviewBack10;
    }
    // Check if the item has a "Number" customization with value "8"
    if (item.customizations.Back && item.customizations.Back.Number === "8") {
      return tshirtPreviewBack8;
    }
    // Return null if no matching back image
    return null;
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      {/* Configuration Panel */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        viewport={{ once: true }}
        className="bg-gray-50 rounded-lg p-6"
      >
        <h3 className="text-2xl font-semibold text-gray-900 mb-6">Configuration</h3>
        
        {/* Configuration Row */}
        <div className="mb-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Map Customization to */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Map Customization to:
              </label>
              <div className="space-y-2">
                {["Item Notes", "Customer Notes", "Item Notes and Customer Notes"].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name="mapTo"
                      value={option}
                      checked={mapTo === option}
                      onChange={(e) => {
                        setMapTo(e.target.value);
                        trackConfigurationChange("veeqo", "mapTo", e.target.value);
                      }}
                      className="mr-3"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Image Display */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Image Display:
              </label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={displayPreviewImage}
                    onChange={(e) => {
                      setDisplayPreviewImage(e.target.checked);
                      trackConfigurationChange("veeqo", "displayPreviewImage", e.target.checked);
                    }}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700">Display Preview Image</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={displayUserUploaded}
                    onChange={(e) => {
                      setDisplayUserUploaded(e.target.checked);
                      trackConfigurationChange("veeqo", "displayUserUploaded", e.target.checked);
                    }}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700">Display User Uploaded</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Include/Exclude Options */}
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-gray-900">Include/Exclude:</h4>
          
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Surface names:
            </label>
            <select
              value={surfaceNames}
              onChange={(e) => {
                setSurfaceNames(e.target.value);
                trackConfigurationChange("veeqo", "surfaceNames", e.target.value);
              }}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="always">Always</option>
              <option value="never">Never</option>
              <option value="only when multiple items">Only when multiple items</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customization field:
            </label>
            <select
              value={customizationField}
              onChange={(e) => {
                setCustomizationField(e.target.value);
                trackConfigurationChange("veeqo", "customizationField", e.target.value);
              }}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="always">Always</option>
              <option value="never">Never</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Item names:
            </label>
            <select
              value={itemNames}
              onChange={(e) => {
                setItemNames(e.target.value);
                trackConfigurationChange("veeqo", "itemNames", e.target.value);
              }}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="always">Always</option>
              <option value="never">Never</option>
              <option value="only when multiple items">Only when multiple items</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity:
            </label>
            <select
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                trackConfigurationChange("veeqo", "quantity", e.target.value);
              }}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="always">Always</option>
              <option value="never">Never</option>
              <option value="only when multiple items">Only when multiple items</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Preview Panel */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        viewport={{ once: true }}
        className="bg-gray-50 rounded-lg"
      >
        {/* View Toggle */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => {
                setActiveView("order");
                trackViewChange("veeqo", "order");
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium ${
                activeView === "order"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Veeqo Order Mock UI
            </button>
            <button
              onClick={() => {
                setActiveView("packing");
                trackViewChange("veeqo", "packing");
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium ${
                activeView === "packing"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Mock Packing Slip
            </button>
          </div>
        </div>

        {/* Order View */}
        {activeView === "order" && (
          <div className="p-6">
            {/* Order Container */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#6296b8] px-4 py-3">
                <h4 className="text-lg font-semibold text-white">ORDER NO. 112-8877665-4433221</h4>
              </div>
              {/* Table header */}
              <div className="bg-gray-50 border-b border-gray-200">
                <div className="flex gap-4 px-4 py-3 text-sm font-bold text-gray-700">
                  <div className="flex-1 text-left">PRODUCT</div>
                  {displayPreviewImage && <div className="text-center flex-shrink-0 w-[120px]">PREVIEW</div>}
                  {displayUserUploaded && <div className="text-center flex-shrink-0 w-[60px]">UPLOAD</div>}
                  <div className="text-center max-w-[50px] flex-shrink-0 w-[40px]">QTY</div>
                </div>
              </div>

              {/* Table body */}
              <div className="bg-white">
                {amazonOrder.items.map((item, index) => (
                  <div key={index} className="border-b border-gray-100 last:border-b-0">
                    {/* Main row */}
                    <div className="flex gap-4 px-4 py-3 items-start">
                      <div className="flex-1">
                        <div className="flex items-start space-x-3">
                          <div className="w-12 h-12 rounded flex-shrink-0">
                            <img 
                              src={tshirtProductImage} 
                              alt={item.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900 mb-2">
                              {item.name}
                            </div>
                            {(mapTo === "Item Notes" || mapTo === "Item Notes and Customer Notes") && (
                              <textarea
                                readOnly
                                className="w-full text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2 resize-none"
                                rows="3"
                                value={veeqoData.itemOptions
                                  .filter(option => option.itemIndex === index)
                                  .map(option => option.content)
                                  .join('\n')}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      {displayPreviewImage && (
                        <div className="flex justify-center flex-shrink-0 w-[120px]">
                          <div className="flex flex-row space-x-1">
                            {/* Front Preview Image */}
                            {getFrontPreviewImage(item) && (
                              <div className="flex flex-col items-center">
                                <img 
                                  src={getFrontPreviewImage(item)} 
                                  alt="Front Preview" 
                                  className="w-16 h-16 object-cover rounded border border-gray-200"
                                />
                                <span className="text-xs text-gray-500">Front</span>
                              </div>
                            )}
                            {/* Back Preview Image */}
                            {getBackPreviewImage(item) && (
                              <div className="flex flex-col items-center">
                                <img 
                                  src={getBackPreviewImage(item)} 
                                  alt="Back Preview" 
                                  className="w-16 h-16 object-cover rounded border border-gray-200"
                                />
                                <span className="text-xs text-gray-500">Back</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {displayUserUploaded && (
                        <div className="flex justify-center flex-shrink-0 w-[60px]">
                          <img 
                            src={userUploadImage} 
                            alt="User Upload" 
                            className="w-12 h-12 object-cover rounded border border-gray-200"
                          />
                        </div>
                      )}
                      <div className="text-center max-w-[50px] flex-shrink-0 w-[40px]">
                        <p className="text-sm font-medium">{item.quantity}</p>
                      </div>
                    </div>


                  </div>
                ))}
              </div>

              {/* Customer Notes section */}
              <div className="mt-6 p-4">
                <div className="bg-gray-100 rounded-lg p-4">
                  <h4 className="text-base font-bold text-gray-900 mb-3 text-center">CUSTOMER NOTES</h4>
                  <textarea
                    readOnly
                    className="w-full text-sm text-gray-600 bg-white border border-gray-200 rounded p-3 resize-none"
                    rows={mapTo === "Customer Notes" || mapTo === "Item Notes and Customer Notes" ? "8" : "4"}
                    placeholder="Add customer note here..."
                    value={mapTo === "Customer Notes" || mapTo === "Item Notes and Customer Notes" ? veeqoData.customerNotes : ""}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Packing Slip View */}
        {activeView === "packing" && (
          <div className="p-6">
            <div className="border border-gray-300 bg-white rounded-lg overflow-hidden">
              {/* Header Section */}
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div className="text-2xl font-bold text-gray-900">Packing Slip</div>
                </div>
              </div>

              {/* Shipping Address and Order Info */}
              <div className="p-4">
                <div className="flex justify-between">
                  {/* Shipping Address */}
                  <div>
                    <div className="font-bold text-gray-900 mb-2">Shipping address:</div>
                    <div className="text-sm text-gray-700">
                      <div>John Smith</div>
                      <div>123 Main Street</div>
                      <div>Anytown, CA, 90210</div>
                      <div>United States</div>
                    </div>
                  </div>

                  {/* Order Info */}
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Order #: 112-8877665-4433221</div>
                    <div className="text-sm text-gray-600">Date: December 15, 2024</div>
                    <div className="text-sm text-gray-600">Payment Method: Other</div>
                    <div className="text-sm text-gray-600">Shipping Method: Standard</div>
                  </div>
                </div>
              </div>

              {/* Products Section */}
              <div className="p-4">
                <div className="mb-4">
                  <div className="grid grid-cols-12 gap-2 text-sm font-bold text-gray-900 pb-2 border-b border-gray-300">
                    <div className="col-span-10">Product</div>
                    <div className="col-span-2 text-center">Qty</div>
                  </div>
                </div>

                {amazonOrder.items.map((item, index) => (
                  <div key={index} className="mb-6 border-b border-gray-200 pb-4 last:border-b-0">
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-10">
                        <div className="flex items-start space-x-3">
                          <img 
                            src={tshirtProductImage} 
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded flex-shrink-0"
                          />
                          {displayPreviewImage && (
                            <div className="flex flex-col space-y-2 flex-shrink-0">
                              {getFrontPreviewImage(item) && (
                                <img 
                                  src={getFrontPreviewImage(item)} 
                                  alt="Front Preview" 
                                  className="w-16 h-16 object-cover rounded border border-gray-200"
                                />
                              )}
                              {getBackPreviewImage(item) && (
                                <img 
                                  src={getBackPreviewImage(item)} 
                                  alt="Back Preview" 
                                  className="w-16 h-16 object-cover rounded border border-gray-200"
                                />
                              )}
                            </div>
                          )}
                          {displayUserUploaded && (
                            <div className="flex flex-col space-y-2 flex-shrink-0">
                              <img 
                                src={userUploadImage} 
                                alt="User Upload" 
                                className="w-16 h-16 object-cover rounded border border-gray-200"
                              />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900 mb-2">{item.name}</div>
                            {(mapTo === "Item Notes" || mapTo === "Item Notes and Customer Notes") && (
                              <div>
                                <div className="text-xs font-medium text-gray-700 mb-1">Notes:</div>
                                <div className="text-xs text-gray-700 bg-gray-50 p-2 rounded whitespace-pre-line">
                                  {veeqoData.itemOptions
                                    .filter(option => option.itemIndex === index)
                                    .map(option => option.content)
                                    .join('\n')}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-2 text-center text-sm font-medium">{item.quantity}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Customer Notes */}
              <div className="p-4 bg-gray-50 border-t border-gray-300">
                <div className="font-bold text-gray-900 mb-2">Customer notes:</div>
                {(mapTo === "Customer Notes" || mapTo === "Item Notes and Customer Notes") && veeqoData.customerNotes && (
                  <div className="text-sm text-gray-700 whitespace-pre-line">{veeqoData.customerNotes}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default VeeqoDemo;
